/*
  Стадия счёта в шапке карточки: ярлык и окно смены (20.10.2026).

  Решение владельца по макету 1Б: смена стадии отделена от сборки пакета.
  Раньше форма смены стояла на вкладке «Суд» над кнопкой «Сформировать пакет», и
  два разных действия лежали в одной карточке: тот, кто собирал пакет, мог
  сдвинуть счёт по стадиям, и наоборот. Теперь стадия живёт там, где её видят, -
  рядом с номером счёта, - а вкладка «Суд» занимается только документами.

  Окно спрашивает ровно то же, что спрашивала форма: новую стадию и причину. Причина
  попадает в журнал действий, без неё смена не уходит. Итог (стадия сменилась или
  сервер назвал, почему нет) уходит странице одним сообщением: ошибки запроса остаются
  внутри окна, рядом с действием (правило 3 скилла `frontend`).
*/

import { useEffect, useState } from 'react'

import { api } from '../api/client.js'
import { STAGE_TITLES } from '../api/format.js'
import { Field, Notice, Tag } from './Common.jsx'
import Modal from './Modal.jsx'
import Dropdown from './Select.jsx'

function StageDialog({ card, initial, onClose, onDone }) {
  const [stage, setStage] = useState(initial)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function submit(event) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const outcome = await api.changeStage(card.account_id, stage, reason.trim())
      onClose()
      onDone(
        outcome.changed ? `Стадия изменена: ${outcome.stage_title}.` : outcome.reason,
        outcome.changed ? 'ok' : 'warn',
      )
    } catch (failure) {
      setError(failure)
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`Сменить стадию счёта № ${card.number}`}
      note={`Сейчас: ${card.stage_title}`}
      onClose={onClose}
      busy={busy}
    >
      <form onSubmit={submit}>
        <div className="modal__text">
          <div className="row">
            <div className="field field--s">
              <span className="field__label" id="stage-label">
                Новая стадия
              </span>
              <Dropdown
                value={stage}
                onChange={setStage}
                options={STAGE_TITLES}
                empty="выберите"
                labelledBy="stage-label"
              />
            </div>
          </div>
          <div className="row">
            <Field label="Причина - попадёт в журнал действий" width="l">
              <input
                className="field__input"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                minLength={3}
                required
              />
            </Field>
          </div>
        </div>
        <Notice error={error} />
        <div className="row row--tight">
          {/* Выключена, пока не выбрана стадия и не написана причина: свой
              выпадающий список браузер не проверяет (правило 6в скилла). */}
          <button
            type="submit"
            className="button"
            disabled={busy || !stage || reason.trim().length < 3}
          >
            Сменить
          </button>
          <button type="button" className="button button--quiet" onClick={onClose} disabled={busy}>
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  )
}

/*
  `suggested` - следующая стадия, подсказанная кнопкой «Отметить: ...» в делах по
  счёту: окно открывается сразу, с выбранной стадией, остаётся вписать причину.
  Отметка времени `at` нужна, чтобы повторное нажатие той же кнопки открыло окно
  снова, хотя стадия та же.
*/
export default function AccountStage({ card, onDone, suggested }) {
  const [open, setOpen] = useState(Boolean(suggested?.stage))
  const [initial, setInitial] = useState(suggested?.stage ?? '')

  useEffect(() => {
    if (suggested?.stage) {
      setInitial(suggested.stage)
      setOpen(true)
    }
  }, [suggested])

  /*
    21.10.2026, замечания владельца: (1) статус прыгал вниз при нажатии кнопки - окно
    лежало ВНУТРИ строки, а правило `.row:has(.field)` выравнивает строку с полем по
    нижнему краю, то есть открытое окно с полями перестраивало строку под собой.
    Окно вынесено из строки. (2) Порядок «кнопка, потом статус», а статус крупнее,
    не ниже кнопки (`tag--lg`): это главное, что человек хочет знать о счёте.
  */
  return (
    <>
      <div className="row row--tight stage-row">
        <button
          type="button"
          className="button button--quiet"
          onClick={() => {
            setInitial('')
            setOpen(true)
          }}
        >
          Сменить стадию…
        </button>
        <Tag kind="calm" large>
          {card.stage_title}
        </Tag>
      </div>
      {open ? (
        <StageDialog
          key={initial}
          card={card}
          initial={initial}
          onClose={() => setOpen(false)}
          onDone={onDone}
        />
      ) : null}
    </>
  )
}
